import { useContext } from 'react';
import { ApolloError, useQuery, ApolloQueryResult, useMutation } from '@apollo/client';
import { useAuth } from '@providers/AuthProvider';
import authenticatedMemberIsVisitWaivable from '@graphql/queries/authenticatedMemberIsVisitWaivable.gql';
import authenticatedMemberWaiveVisit from '@graphql/mutations/WaiveVisit.mutation.gql';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

export interface IMemberIsVisitWaivable {
	isWaivable: boolean;
}

export interface IMemberWaiveVisit {
	waiverFailureReason?: string;
	waived: boolean;
}

export interface IMemberInvoiceProposal {
	isWaivable: boolean;
}

interface IAuthenticatedMemberIsVisitWaivable {
	authenticatedMember: {
		isVisitWaivable: IMemberIsVisitWaivable;
	};
}

type UseMemberIsVisitWaivable = (serviceType?: string) => {
	loading: boolean;
	data?: IMemberIsVisitWaivable;
	error?: ApolloError;
	refetch: () => Promise<ApolloQueryResult<IAuthenticatedMemberIsVisitWaivable>>;
	waiveVisit: (
		serviceType: string,
		appointmentId: string,
		flowSessionUuid: string
	) => Promise<IMemberWaiveVisit>;
};

export const useMemberIsVisitWaivable: UseMemberIsVisitWaivable = (visitServiceType) => {
	const { loggedIn } = useAuth();
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const [waiveVisit] = useMutation(authenticatedMemberWaiveVisit, { client: graphQLv2Client });
	const { loading, error, data, refetch } = useQuery<IAuthenticatedMemberIsVisitWaivable>(
		authenticatedMemberIsVisitWaivable,
		{
			variables: {
				serviceType: visitServiceType,
			},
			client: graphQLv2Client,
			skip: !loggedIn,
		}
	);
	return {
		loading,
		data: loading ? undefined : data?.authenticatedMember.isVisitWaivable,
		error: error,
		refetch: refetch,
		waiveVisit: async (appointmentId: string, serviceType: string, flowSessionUuid: string) => {
			const result = await waiveVisit({
				variables: {
					serviceType,
					appointmentId,
					flowSessionUuid,
				},
			}).catch((error) => {
				console.log(error);
				return error;
			});
			return {
				waiverFailureReason: result?.data?.waiveVisit?.waiverFailureReason,
				waived: result?.data?.waiveVisit?.waived,
			};
		},
	};
};
